import React, { useState } from 'react';
import { CreditCardIcon, HeartIcon, GiftIcon } from '@heroicons/react/24/outline';
import PaymentService from '../../services/paymentService';

const DonationForm = () => {
  const [formData, setFormData] = useState({
    amount: '',
    customAmount: '',
    category: PaymentService.DonationCategories.GENERAL,
    donorName: '',
    email: '',
    phone: '',
    message: '',
    anonymous: false,
    recurring: false,
    paymentMethod: 'card'
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const predefinedAmounts = [25, 50, 100, 250, 500, 1000];

  const donationCategories = [
    { value: PaymentService.DonationCategories.GENERAL, label: 'General Fund', icon: HeartIcon },
    { value: PaymentService.DonationCategories.BOOSTER_CLUB, label: 'Booster Club', icon: GiftIcon },
    { value: PaymentService.DonationCategories.EQUIPMENT, label: 'Equipment Fund', icon: CreditCardIcon },
    { value: PaymentService.DonationCategories.SCHOLARSHIPS, label: 'Scholarships', icon: HeartIcon },
    { value: PaymentService.DonationCategories.FACILITIES, label: 'Facilities', icon: CreditCardIcon }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAmountSelect = (amount) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString(),
      customAmount: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const donationAmount = formData.customAmount || formData.amount;
      
      const donationData = {
        amount: parseFloat(donationAmount),
        category: formData.category,
        donorInfo: {
          name: formData.donorName,
          email: formData.email,
          phone: formData.phone,
          anonymous: formData.anonymous
        },
        message: formData.message,
        recurring: formData.recurring,
        paymentMethod: formData.paymentMethod
      };

      await PaymentService.processDonation(donationData);
      setSuccess(true);
      
      // Reset form
      setFormData({
        amount: '',
        customAmount: '',
        category: PaymentService.DonationCategories.GENERAL,
        donorName: '',
        email: '',
        phone: '',
        message: '',
        anonymous: false,
        recurring: false,
        paymentMethod: 'card'
      });
    } catch (err) {
      setError(err.message || 'Failed to process donation');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <HeartIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Thank You!</h3>
          <p className="mt-2 text-sm text-gray-500">
            Your donation has been processed successfully. You will receive a confirmation email shortly.
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Make Another Donation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <HeartIcon className="mx-auto h-12 w-12 text-blue-600" />
        <h2 className="mt-4 text-2xl font-bold text-gray-900">Support East End Prep Athletics</h2>
        <p className="mt-2 text-gray-600">
          Your donation helps provide equipment, facilities, and opportunities for our student-athletes.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Donation Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Donation Category
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {donationCategories.map((category) => {
              const IconComponent = category.icon;
              return (
                <label
                  key={category.value}
                  className={`relative flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                    formData.category === category.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={category.value}
                    checked={formData.category === category.value}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <IconComponent className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    {category.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Donation Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Donation Amount
          </label>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {predefinedAmounts.map((amount) => (
              <button
                key={amount}
                type="button"
                onClick={() => handleAmountSelect(amount)}
                className={`p-3 border rounded-lg text-center hover:bg-gray-50 ${
                  formData.amount === amount.toString()
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700'
                }`}
              >
                ${amount}
              </button>
            ))}
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
            <input
              type="number"
              name="customAmount"
              value={formData.customAmount}
              onChange={handleInputChange}
              placeholder="Enter custom amount"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="1"
              step="0.01"
            />
          </div>
        </div>

        {/* Donor Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="donorName" className="block text-sm font-medium text-gray-700">
              Full Name *
            </label>
            <input
              type="text"
              id="donorName"
              name="donorName"
              value={formData.donorName}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone Number (Optional)
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700">
            Message (Optional)
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={3}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Leave a message with your donation..."
          />
        </div>

        {/* Options */}
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="anonymous"
              checked={formData.anonymous}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Make this donation anonymous
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="recurring"
              checked={formData.recurring}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Make this a monthly recurring donation
            </span>
          </label>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || (!formData.amount && !formData.customAmount)}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </div>
          ) : (
            `Donate ${PaymentService.formatCurrency(
              parseFloat(formData.customAmount || formData.amount || 0)
            )}`
          )}
        </button>
      </form>
    </div>
  );
};

export default DonationForm;